import React, { useCallback, useEffect, useRef, useState, memo } from "react";
import { Fade } from "react-awesome-reveal";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { Popover } from "react-tiny-popover";
import { PurpleButton } from "../../../../../Components/Buttons/PurpleButton";
import { WhiteButton } from "../../../../../Components/Buttons/WhiteButton";
import { NumberSpin } from "../../../../../Components/Spin/NumberSpin";
import {
  PinIcon,
  MapShareIcon,
  MapPlusIcon,
  MapMinusIcon,
  MintGuestsIcon,
  MintBedroomsIcon,
  MintBedsIcon,
  MintBathroomsIcon,
} from "../../../../../AssetComponents/Images";
import { mapStyles } from "../../../../../Components/GoogleMap/Style";
import mapPinIcon from "../../../../../assets/images/Dashboard/Dashboard/mappin.svg";
import Meters from "../../../../../assets/images/Dashboard/Dashboard/Meters";
import Address from "../../../../../assets/images/Dashboard/Dashboard/Address";

const MintBasicInfoPage = memo(({
  addressInput,
  setAddressInput,
  pinLocation,
  setPinLocation,
  zoom,
  setZoom,
  predictList,
  setPredictList,
  isOpen,
  setIsOpen,
  addressDetails,
  setAddressDetails,
  handleSelectFromAddressList,
  handleSelectMapPin,
  getAddressList,
  metaDetails,
  setMetaDetails,
  onBack,
  onNext,
}) => {
  const [isDesktop, setIsDesktop] = useState(false);
  const isUserTypingRef = useRef(false);
  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== "undefined") {
        setIsDesktop(window.innerWidth >= 768);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Memoized input change handler
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    
    // Only set typing flag if user is actually typing (not programmatic updates)
    if (value !== addressInput) {
      isUserTypingRef.current = true;
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      typingTimeoutRef.current = setTimeout(() => {
        isUserTypingRef.current = false;
      }, 1000);
    }
    
    setAddressInput(value);
    setIsOpen(true);
    
    if (value.length > 2) {
      getAddressList(value);
    } else {
      setPredictList([]);
    }
  }, [addressInput, setAddressInput, setIsOpen, getAddressList, setPredictList]);

  // Memoized marker drag handler
  const handleMarkerDragEnd = useCallback((e) => {
    const newLocation = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };
    setPinLocation(newLocation);
    
    // Only geocode when user is NOT typing
    if (!isUserTypingRef.current) {
      handleSelectMapPin(newLocation);
    }
  }, [setPinLocation, handleSelectMapPin]);

  return (
    <div className="flex flex-col w-full h-full">
      {/* Main Content */}
      <div className="flex-1 flex flex-col md:flex-row gap-4 md:gap-8 w-full px-4 md:px-8 py-4 md:py-6">
        {/* Map Section */}
        <div className="flex flex-col items-center space-y-[10px] md:space-y-[20px] w-full md:w-1/2 pt-0">
          <div className="text-[18px] md:text-[20px] font-semibold text-center">
            Pin point property location
          </div>
          <div className="text-center text-sm md:text-base">Move the pin to your location</div>

          {/* Address Input Field - Mobile */}
          <div className="w-full md:hidden">
            <Popover
              isOpen={predictList?.length > 0 && isOpen && !isDesktop}
              positions={"bottom"}
              onClickOutside={() => setPredictList([])}
              content={
                <div className="py-[10px] space-y-[4px] bg-white rounded-[8px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                  {predictList?.map((address) => (
                    <div
                      key={address.place_id}
                      onClick={() => handleSelectFromAddressList(address.place_id)}
                      className="w-full bg-white flex gap-[10px] items-center cursor-pointer overflow-hidden h-[50px] p-[8px] rounded-[8px] hover:bg-gray-300"
                    >
                      <PinIcon />
                      <div className="w-full ">{address.description}</div>
                    </div>
                  ))}
                </div>
              }
            >
              <div className="bg-white border-[2px] border-[#E3E3E3] rounded-[12px] p-4 shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-sm text-[#374151]">Address</div>
                  <input
                    className="outline-none w-full text-[#666666] font-normal text-sm flex-1 text-right pl-2 min-h-[20px] flex items-center justify-end overflow-hidden"
                    placeholder="Select location on map"
                    value={addressInput || ""}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </Popover>
          </div>

          {/* Address Input Field - Desktop */}
          <div className="hidden md:block">
            <Popover
              isOpen={predictList?.length > 0 && isOpen && isDesktop}
              positions={"bottom"}
              onClickOutside={() => setPredictList([])}
              content={
                <div className="py-[10px] space-y-[4px] bg-white rounded-[8px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                  {predictList?.map((address) => (
                    <div
                      key={address.place_id}
                      onClick={() => handleSelectFromAddressList(address.place_id)}
                      className="w-full md:w-[500px] bg-white flex gap-[10px] items-center cursor-pointer overflow-hidden h-[50px] p-[8px] rounded-[8px] hover:bg-gray-300"
                    >
                      <PinIcon />
                      <div className="w-full ">{address.description}</div>
                    </div>
                  ))}
                </div>
              }
            >
              <div className="bg-white border-[2px] border-[#E3E3E3] rounded-[12px] p-4 shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] w-full max-w-[500px]">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-sm text-[#374151]">Address</div>
                  <input
                    className="outline-none w-full text-[#666666] font-normal text-sm flex-1 text-right pl-2 min-h-[20px] flex items-center justify-end overflow-hidden"
                    placeholder="Select location on map"
                    value={addressInput || ""}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </Popover>
          </div>

          <GoogleMap
            center={pinLocation}
            zoom={zoom}
            mapContainerClassName="w-full max-w-[280px] md:max-w-[500px] h-[250px] md:h-[600px] rounded-[8px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
            options={{
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: false,
              zoomControl: false,
              styles: mapStyles,
            }}
          >
            <div className="absolute top-[10px] md:top-[20px] right-[10px] md:right-[20px] flex items-center gap-[8px] md:gap-[10px]">
              <div className="p-[3px] md:p-[4px] rounded-full hover:bg-[#000000] shadow-md bg-gradient-to-b from-[#202020c0] from-0% to-70% to-[#202020]">
                <MapShareIcon />
              </div>
              <div
                className="p-[3px] md:p-[4px] rounded-full shadow-md bg-white hover:bg-[#f6f6f6] cursor-pointer"
                onClick={() => setZoom(zoom + 1)}
              >
                <MapPlusIcon />
              </div>
              <div
                className="p-[3px] md:p-[4px] rounded-full shadow-md bg-white hover:bg-[#f6f6f6] cursor-pointer"
                onClick={() => setZoom(zoom - 1)}
              >
                <MapMinusIcon />
              </div>
            </div>

            <Marker
              position={pinLocation}
              draggable
              onDragEnd={handleMarkerDragEnd}
              icon={{
                url: mapPinIcon,
                scaledSize: new window.google.maps.Size(60, 60),
                origin: new window.google.maps.Point(0, 0),
                anchor: new window.google.maps.Point(30, 51),
              }}
            />
          </GoogleMap>
        </div>

        {/* Address Confirmation Section */}
        <div className="flex flex-col space-y-[20px] w-full md:w-1/2">
          <div className="p-[16px] md:p-[20px] bg-white border-[2px] border-[#E3E3E3] rounded-[10px] flex flex-col items-center space-y-[12px] md:space-y-[16px]">
            <Address />
            <div className="font-semibold text-[18px] md:text-[20px] text-center">
              Confirm your address
            </div>
            <div className="text-[#595959] text-center text-sm md:text-base max-w-[280px] md:max-w-[300px]">
              Your address is only shared with guest after they've made a reservation
            </div>

            <div className="flex flex-col space-y-[10px] md:space-y-[12px] w-full">
              {/* Mobile Address Details - Enhanced for Readability */}
              <div className="md:hidden space-y-[10px] w-full">
                {/* Full Address Display - Prominent */}
                <div className="bg-gradient-to-r from-[#F8FAFC] to-[#F1F5F9] rounded-[12px] p-4 border border-[#E2E8F0] shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 bg-[#8B5CF6] rounded-full"></div>
                    <span className="text-[#374151] text-sm font-semibold">Full Address</span>
                  </div>
                  <div className="text-[#1F2937] text-sm leading-relaxed min-h-[50px] flex items-start">
                    <span className="break-words">{addressInput || "No address selected"}</span>
                  </div>
                </div>

                {/* Address Components Grid */}
                <div className="grid grid-cols-1 gap-3">
                  <div className="bg-white rounded-[10px] p-3 border border-[#E5E7EB] shadow-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-1.5 h-1.5 bg-[#10B981] rounded-full"></div>
                      <span className="text-[#6B7280] text-xs font-medium">Street</span>
                    </div>
                    <div className="text-[#374151] text-sm font-medium">
                      {addressDetails?.street || "-"}
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-[10px] p-3 border border-[#E5E7EB] shadow-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-1.5 h-1.5 bg-[#3B82F6] rounded-full"></div>
                      <span className="text-[#6B7280] text-xs font-medium">City</span>
                    </div>
                    <div className="text-[#374151] text-sm font-medium">
                      {addressDetails?.city || "-"}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-[10px] p-3 border border-[#E5E7EB] shadow-sm">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full"></div>
                        <span className="text-[#6B7280] text-xs font-medium">State</span>
                      </div>
                      <div className="text-[#374151] text-sm font-medium">
                        {addressDetails?.state || "-"}
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-[10px] p-3 border border-[#E5E7EB] shadow-sm">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-1.5 h-1.5 bg-[#8B5CF6] rounded-full"></div>
                        <span className="text-[#6B7280] text-xs font-medium">Zip Code</span>
                      </div>
                      <div className="text-[#374151] text-sm font-medium">
                        {addressDetails?.zipCode || "-"}
                      </div>
                    </div>
                  </div>

                  {addressDetails?.county && (
                    <div className="bg-white rounded-[10px] p-3 border border-[#E5E7EB] shadow-sm">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-1.5 h-1.5 bg-[#EF4444] rounded-full"></div>
                        <span className="text-[#6B7280] text-xs font-medium">County / Regional</span>
                      </div>
                      <div className="text-[#374151] text-sm font-medium">
                        {addressDetails?.county}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Desktop Address Details */}
              <div className="hidden md:flex flex-col space-y-[10px] md:space-y-[12px] w-full">
                <div className="flex items-center justify-between p-[12px] md:p-[14px] rounded-[12px] w-full border-[2px] border-[#E3E3E3] bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] h-[70px] md:h-[80px]">
                  <div className="font-medium text-sm md:text-base w-[120px] md:w-[160px] flex-shrink-0">Street</div>
                  <div className="text-[#666666] font-normal text-sm md:text-base flex-1 text-right pl-2 min-h-[20px] md:min-h-[24px] flex items-center justify-end overflow-hidden">
                    <span className="truncate w-full text-right">{addressDetails?.street || "-"}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-[12px] md:p-[14px] rounded-[12px] w-full border-[2px] border-[#E3E3E3] bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] h-[70px] md:h-[80px]">
                  <div className="font-medium text-sm md:text-base w-[120px] md:w-[160px] flex-shrink-0">City</div>
                  <div className="text-[#666666] font-normal text-sm md:text-base flex-1 text-right pl-2 min-h-[20px] md:min-h-[24px] flex items-center justify-end overflow-hidden">
                    <span className="truncate w-full text-right">{addressDetails?.city || "-"}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-[12px] md:p-[14px] rounded-[12px] w-full border-[2px] border-[#E3E3E3] bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] h-[70px] md:h-[80px]">
                  <div className="font-medium text-sm md:text-base w-[120px] md:w-[160px] flex-shrink-0">State (optional)</div>
                  <div className="text-[#666666] font-normal text-sm md:text-base flex-1 text-right pl-2 min-h-[20px] md:min-h-[24px] flex items-center justify-end overflow-hidden">
                    <span className="truncate w-full text-right">{addressDetails?.state || "-"}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-[12px] md:p-[14px] rounded-[12px] w-full border-[2px] border-[#E3E3E3] bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] h-[70px] md:h-[80px]">
                  <div className="font-medium text-sm md:text-base w-[120px] md:w-[160px] flex-shrink-0">Zip code (optional)</div>
                  <div className="text-[#666666] font-normal text-sm md:text-base flex-1 text-right pl-2 min-h-[20px] md:min-h-[24px] flex items-center justify-end overflow-hidden">
                    <span className="truncate w-full text-right">{addressDetails?.zipCode || "-"}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-[12px] md:p-[14px] rounded-[12px] w-full border-[2px] border-[#E3E3E3] bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] h-[70px] md:h-[80px]">
                  <div className="font-medium text-sm md:text-base w-[120px] md:w-[160px] flex-shrink-0">County / Regional</div>
                  <div className="text-[#666666] font-normal text-sm md:text-base flex-1 text-right pl-2 min-h-[20px] md:min-h-[24px] flex items-center justify-end overflow-hidden">
                    <span className="truncate w-full text-right">{addressDetails?.county || "-"}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons - Fixed at Bottom */}
      <div className="w-full flex flex-row gap-3 md:justify-between items-center px-4 md:px-8 py-3 md:py-6 bg-white border-t border-gray-200 shadow-lg">
        <WhiteButton  
          text="Back"
          onClick={onBack} 
          className="flex-1 md:w-[120px] h-[45px] md:h-[50px] flex items-center justify-center text-sm md:text-base font-medium"
        />
        <PurpleButton
          text="Next"
          onClick={onNext}
          className="flex-1 md:w-[140px] h-[45px] md:h-[50px] flex items-center justify-center text-sm md:text-base font-medium"
        />
      </div>
    </div>
  );
});

MintBasicInfoPage.displayName = "MintBasicInfoPage";

export default MintBasicInfoPage;
